import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Layers, RefreshCw, TrendingUp, TrendingDown, Clock, Target,
  ShieldAlert, Zap, AlertTriangle, ArrowUpRight, ArrowDownRight,
  Timer, Activity, XCircle, BarChart3, Gauge, Eye, Volume2,
  DollarSign, Flame, CircleDot
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function LiveTimer({ date }) {
  const [elapsed, setElapsed] = useState('');
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!date) return;
    const update = () => {
      const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
      setSeconds(diff);
      if (diff < 60) setElapsed(`${diff}s`);
      else if (diff < 3600) setElapsed(`${Math.floor(diff/60)}m ${diff%60}s`);
      else setElapsed(`${Math.floor(diff/3600)}h ${Math.floor((diff%3600)/60)}m`);
    };
    update();
    const i = setInterval(update, 1000);
    return () => clearInterval(i);
  }, [date]);

  const urgencyColor = seconds < 300 ? 'text-emerald-400' : seconds < 1800 ? 'text-[hsl(var(--primary))]' : 'text-red-400';

  return (
    <div className="flex items-center gap-1.5">
      <div className={`relative flex items-center justify-center`}>
        <span className={`absolute w-2 h-2 rounded-full ${seconds < 300 ? 'bg-emerald-400' : seconds < 1800 ? 'bg-[hsl(var(--primary))]' : 'bg-red-400'} animate-ping opacity-40`} />
        <span className={`w-2 h-2 rounded-full ${seconds < 300 ? 'bg-emerald-400' : seconds < 1800 ? 'bg-[hsl(var(--primary))]' : 'bg-red-400'}`} />
      </div>
      <span className={`font-mono text-xs tabular-nums font-medium ${urgencyColor}`}>{elapsed}</span>
    </div>
  );
}

function ExpiryCountdown({ expiry }) {
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    if (!expiry) return;
    const update = () => {
      const exp = new Date(expiry + 'T16:00:00').getTime();
      const diff = Math.max(0, Math.floor((exp - Date.now()) / 1000));
      if (diff <= 0) { setRemaining('EXPIRED'); return; }
      const d = Math.floor(diff / 86400);
      const h = Math.floor((diff % 86400) / 3600);
      const m = Math.floor((diff % 3600) / 60);
      if (d > 0) setRemaining(`${d}d ${h}h`);
      else if (h > 0) setRemaining(`${h}h ${m}m`);
      else setRemaining(`${m}m`);
    };
    update();
    const i = setInterval(update, 60000);
    return () => clearInterval(i);
  }, [expiry]);

  return (
    <span className={`font-mono text-[10px] tabular-nums ${remaining === 'EXPIRED' ? 'text-red-400' : 'text-[hsl(var(--muted-foreground))]'}`}>
      {remaining || '--'}
    </span>
  );
}

function OptionTile({ opt, onExpire }) {
  const isCall = opt.contract_type === 'CALL';
  const successPct = opt.success_probability || opt.confidence || 0;
  const isExpired = opt.status === 'expired';

  const handleExpire = async (e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/api/options/${opt._id}/expire`, { method: 'POST' });
      if (res.ok) { toast.success(`${opt.symbol} option moved to expired`); if (onExpire) onExpire(); }
    } catch { toast.error('Network error'); }
  };

  const borderColor = isExpired
    ? 'border-[hsl(var(--border))]'
    : isCall ? 'border-emerald-500/25 hover:border-emerald-500/40' : 'border-red-500/25 hover:border-red-500/40';

  const glowClass = !isExpired && successPct >= 70
    ? (isCall ? 'hover:shadow-[0_0_40px_-15px_rgba(46,196,142,0.2)]' : 'hover:shadow-[0_0_40px_-15px_rgba(220,80,80,0.2)]')
    : '';

  return (
    <div className={`relative rounded-xl border bg-[hsl(var(--card))] transition-all duration-200 ${borderColor} ${glowClass} ${isExpired ? 'opacity-60' : ''}`} data-testid="option-tile">
      {/* Top accent line */}
      <div className={`absolute top-0 left-4 right-4 h-[2px] rounded-b ${isCall ? 'bg-emerald-500/40' : 'bg-red-500/40'} ${isExpired ? 'opacity-30' : ''}`} />

      <div className="p-4 pt-5">
        {/* Header Row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xl font-black tracking-wide">{opt.symbol}</span>
            <Badge className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
              isCall ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' :
              'bg-red-500/15 text-red-400 border border-red-500/30'
            }`}>
              {isCall ? <ArrowUpRight className="w-3 h-3 mr-0.5" /> : <ArrowDownRight className="w-3 h-3 mr-0.5" />}
              {opt.contract_type}
            </Badge>
            {!isExpired && (
              <Badge variant="outline" className={`text-[10px] ${
                opt.urgency === 'IMMEDIATE' ? 'text-red-400 border-red-500/30 bg-red-500/5' :
                opt.urgency === 'WITHIN_HOUR' ? 'text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5' :
                'text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]'
              }`}>
                <Flame className="w-2.5 h-2.5 mr-0.5" />{opt.urgency}
              </Badge>
            )}
          </div>
          {!isExpired ? <LiveTimer date={opt.created_at} /> : (
            <span className="text-[10px] text-red-400/70">Expired</span>
          )}
        </div>

        {/* Strike + Expiry Row */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-1.5">
            <DollarSign className="w-3 h-3 text-[hsl(var(--muted-foreground))]" />
            <span className="text-xs text-[hsl(var(--muted-foreground))]">Strike</span>
            <span className="font-mono text-sm font-bold tabular-nums">${opt.strike?.toFixed(2)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3 text-[hsl(var(--muted-foreground))]" />
            <span className="text-xs text-[hsl(var(--muted-foreground))]">Exp</span>
            <span className="font-mono text-sm font-semibold tabular-nums">{opt.expiry}</span>
            <ExpiryCountdown expiry={opt.expiry} />
          </div>
        </div>

        {/* Price Grid - Premium / Target / Stop */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center p-2.5 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mb-0.5 uppercase tracking-wider">Premium</p>
            <p className="font-mono text-base font-bold tabular-nums">${opt.buy_price?.toFixed(2)}</p>
          </div>
          <div className="text-center p-2.5 rounded-lg bg-emerald-500/5 border border-emerald-500/15">
            <p className="text-[10px] text-emerald-400 mb-0.5 uppercase tracking-wider">Target</p>
            <p className="font-mono text-base font-bold tabular-nums text-emerald-400">${opt.target_price?.toFixed(2)}</p>
          </div>
          <div className="text-center p-2.5 rounded-lg bg-red-500/5 border border-red-500/15">
            <p className="text-[10px] text-red-400 mb-0.5 uppercase tracking-wider">Stop</p>
            <p className="font-mono text-base font-bold tabular-nums text-red-400">${opt.stop_loss?.toFixed(2)}</p>
          </div>
        </div>

        {/* Key Metrics Row */}
        <div className="grid grid-cols-4 gap-1.5 mb-3">
          <MetricPill icon={Gauge} label="Success" value={`${successPct}%`}
            color={successPct >= 70 ? 'emerald' : successPct >= 50 ? 'gold' : 'red'} />
          <MetricPill icon={AlertTriangle} label="Risk" value={opt.risk_level}
            color={opt.risk_level === 'LOW' ? 'emerald' : opt.risk_level === 'HIGH' ? 'red' : 'gold'} />
          <MetricPill icon={TrendingUp} label="Return" value={`+${Number(opt.estimated_return_pct || 0).toFixed(0)}%`} color="emerald" />
          <MetricPill icon={BarChart3} label="IV" value={opt.implied_volatility ? `${opt.implied_volatility}%` : '--'} color="muted" />
        </div>

        {/* Extra Data Row */}
        <div className="flex items-center gap-3 mb-3 text-[10px] text-[hsl(var(--muted-foreground))]">
          {opt.stock_price > 0 && (
            <span className="flex items-center gap-1">
              <CircleDot className="w-3 h-3" />Stock: <strong className="text-[hsl(var(--foreground))] font-mono">${opt.stock_price?.toFixed(2)}</strong>
            </span>
          )}
          {opt.volume > 0 && (
            <span className="flex items-center gap-1">
              <Volume2 className="w-3 h-3" />Vol: <strong className="font-mono">{opt.volume?.toLocaleString()}</strong>
            </span>
          )}
          {opt.open_interest > 0 && (
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />OI: <strong className="font-mono">{opt.open_interest?.toLocaleString()}</strong>
            </span>
          )}
          {opt.bid > 0 && opt.ask > 0 && (
            <span className="flex items-center gap-1">
              Bid/Ask: <strong className="font-mono">${opt.bid?.toFixed(2)}/${opt.ask?.toFixed(2)}</strong>
            </span>
          )}
        </div>

        {/* Success Probability Bar */}
        <div className="mb-3">
          <div className="h-1.5 rounded-full bg-[hsl(var(--surface-2))] overflow-hidden">
            <div className={`h-full rounded-full confidence-fill ${
              successPct >= 70 ? 'bg-emerald-500' : successPct >= 50 ? 'bg-[hsl(var(--primary))]' : 'bg-red-500'
            }`} style={{ width: `${Math.min(successPct, 100)}%` }} />
          </div>
        </div>

        {/* Thesis */}
        <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed mb-3 line-clamp-2">{opt.thesis}</p>

        {/* Signals */}
        {opt.key_signals?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {opt.key_signals.map((s, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]">{s}</span>
            ))}
          </div>
        )}

        {/* Expire reason for expired options */}
        {isExpired && opt.expire_reason && (
          <p className="text-[10px] text-red-400/70 mb-2">
            {opt.expire_reason}
          </p>
        )}
        {isExpired && opt.expired_at && (
          <p className="text-[10px] text-[hsl(var(--muted-foreground))]">
            Expired: {new Date(opt.expired_at).toLocaleString()}
          </p>
        )}

        {/* Action */}
        {!isExpired && (
          <div className="flex items-center justify-between pt-2 border-t border-[hsl(var(--border))]/50">
            <Button size="sm" variant="ghost" onClick={handleExpire}
              className="text-[hsl(var(--muted-foreground))] text-xs h-7 px-2 hover:text-red-400">
              <XCircle className="w-3 h-3 mr-1" />Remove
            </Button>
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
              {opt.in_the_money ? 'ITM' : 'OTM'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricPill({ icon: Icon, label, value, color }) {
  const colors = {
    emerald: 'text-emerald-400 bg-emerald-500/5 border-emerald-500/15',
    red: 'text-red-400 bg-red-500/5 border-red-500/15',
    gold: 'text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 border-[hsl(var(--primary))]/15',
    muted: 'text-[hsl(var(--muted-foreground))] bg-[hsl(var(--surface-2))] border-[hsl(var(--border))]',
  };
  return (
    <div className={`flex flex-col items-center p-1.5 rounded-lg border ${colors[color]}`}>
      <Icon className="w-3 h-3 mb-0.5" />
      <span className="font-mono text-[11px] font-bold tabular-nums">{value}</span>
      <span className="text-[8px] uppercase tracking-wider opacity-70">{label}</span>
    </div>
  );
}

export default function Options() {
  const [activeOptions, setActiveOptions] = useState([]);
  const [expiredOptions, setExpiredOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [tab, setTab] = useState('active');
  const autoTriggeredRef = React.useRef(false);

  const fetchOptions = useCallback(async () => {
    try {
      const [activeRes, expiredRes] = await Promise.all([
        fetch(`${API}/api/options/active`),
        fetch(`${API}/api/options/all?status=expired&limit=20`),
      ]);
      if (activeRes.ok) {
        const data = await activeRes.json();
        setActiveOptions(data.options || []);
      }
      if (expiredRes.ok) {
        const data = await expiredRes.json();
        setExpiredOptions(data.options || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  // Auto-trigger if no active options
  useEffect(() => {
    if (!loading && activeOptions.length === 0 && !analyzing && !autoTriggeredRef.current) {
      autoTriggeredRef.current = true;
      setAnalyzing(true);
      fetch(`${API}/api/options/analyze`, { method: 'POST' })
        .then(res => { if (res.ok) { toast.success('Options analysis complete!'); fetchOptions(); } })
        .catch(() => {})
        .finally(() => setAnalyzing(false));
    }
  }, [loading, activeOptions.length, analyzing, fetchOptions]);

  useEffect(() => {
    fetchOptions();
    const interval = setInterval(fetchOptions, 30000);
    return () => clearInterval(interval);
  }, [fetchOptions]);

  const triggerOptionsAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/options/analyze`, { method: 'POST' });
      if (res.ok) { toast.success('Options analysis complete!'); fetchOptions(); }
      else { const err = await res.json(); toast.error(err.detail || 'Analysis failed'); }
    } catch { toast.error('Network error'); }
    finally { setAnalyzing(false); }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-14 w-full rounded-xl bg-[hsl(var(--surface-2))]" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-72 rounded-xl bg-[hsl(var(--surface-2))]" />)}
        </div>
      </div>
    );
  }

  const displayOptions = tab === 'active' ? activeOptions : expiredOptions;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="options-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Layers className="w-6 h-6 text-[hsl(var(--primary))]" />Options Trading
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">
            Time-critical options signals with live monitoring
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] px-3 py-1.5 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
            <span className="relative flex items-center justify-center">
              <span className="absolute w-2 h-2 rounded-full bg-emerald-400 animate-ping opacity-40" />
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
            </span>
            Live 30s
          </div>
          <Button onClick={triggerOptionsAnalysis} disabled={analyzing}
            className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 text-sm h-9"
            data-testid="analyze-options-button">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${analyzing ? 'animate-spin' : ''}`} />
            {analyzing ? 'Scanning...' : 'Scan Options'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} data-testid="options-tabs">
        <TabsList className="bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]">
          <TabsTrigger value="active" className="text-xs data-[state=active]:bg-[hsl(var(--accent))]">
            Active ({activeOptions.length})
          </TabsTrigger>
          <TabsTrigger value="expired" className="text-xs data-[state=active]:bg-[hsl(var(--accent))]">
            Expired ({expiredOptions.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Options Grid */}
      {displayOptions.length === 0 ? (
        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-10 text-center">
          {analyzing ? (
            <>
              <RefreshCw className="w-10 h-10 mx-auto mb-3 text-[hsl(var(--primary))] animate-spin" />
              <h3 className="text-base font-medium mb-1">Scanning options chains...</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                AI agents analyzing real-time options data. 30-60 seconds...
              </p>
            </>
          ) : (
            <>
              <Layers className="w-10 h-10 mx-auto mb-3 text-[hsl(var(--muted-foreground))] opacity-30" />
              <h3 className="text-base font-medium mb-1">
                {tab === 'active' ? 'No active options trades' : 'No expired trades yet'}
              </h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
                {tab === 'active' ? 'Click "Scan Options" to analyze options chains.' : 'Expired options appear here for reference.'}
              </p>
              {tab === 'active' && (
                <Button onClick={triggerOptionsAnalysis} disabled={analyzing} size="sm"
                  className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                  <Zap className="w-3 h-3 mr-1" />Scan Now
                </Button>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {displayOptions.map((opt) => <OptionTile key={opt._id} opt={opt} onExpire={fetchOptions} />)}
        </div>
      )}
    </div>
  );
}
